import { FC, ReactNode } from 'react';
import { css, Interpolation, Theme } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { breakpoints } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';
import { BGColorMap, BorderColorMap, PromoColorTheme } from './constant';
import { BadgeType, PromoBannerBadge } from './promo-banner-badge';

interface PromoBannerProps {
  color?: PromoColorTheme;
  badge?: BadgeType;
  containerStyles?: Interpolation<Theme>;
  cardStyles?: Interpolation<Theme>;
  innerRef?: React.Ref<HTMLDivElement>;
  dismissTrackingId?: string;
  onDismiss?: (e?: React.MouseEvent<HTMLButtonElement>) => void;
  title?: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  responsiveBadge?: boolean; // if true, badge will be on a separate line on small screens
  responsiveActions?: boolean; // if true, actions will be on a separate line on small screens
}

export const PromoBanner: FC<PromoBannerProps> = ({
  badge,
  containerStyles,
  cardStyles,
  innerRef,
  color = 'seaweed',
  dismissTrackingId,
  onDismiss,
  title,
  description,
  actions,
  responsiveBadge = false,
  responsiveActions = true,
}) => {
  const { t } = useTranslation('highValueAdoption');

  return (
    <div css={[defaultStyles.containerLayout, containerStyles]} ref={innerRef}>
      <div css={[defaultStyles.card(color, responsiveActions), cardStyles]}>
        <div css={defaultStyles.contentContainer(responsiveBadge)}>
          <PromoBannerBadge badgeType={badge} />
          <div css={defaultStyles.content}>
            {title}
            {description}
          </div>
        </div>

        <div css={defaultStyles.actionBar}>
          {!!onDismiss && (
            <Button
              variant='tertiary'
              onClick={onDismiss}
              trackingId={dismissTrackingId}
              css={responsiveActions ? defaultStyles.responsiveDismissBtn : { display: 'none' }}
            >
              {t('Dismiss')}
            </Button>
          )}
          {actions}
          {!!onDismiss && (
            <Button
              variant='secondary'
              onClick={onDismiss}
              trackingId={dismissTrackingId}
              iconName='x-small'
              aria-label={t('Dismiss')}
              css={responsiveActions ? defaultStyles.responsiveDismissIconBtn : undefined}
            />
          )}
        </div>
      </div>
    </div>
  );
};

const defaultStyles = {
  containerLayout: css`
    display: flex;
    flex-direction: column;
    container-type: inline-size;
  `,
  card: (color: PromoColorTheme, responsive: boolean) => css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: ${theme.spacing(2)};
    padding: ${theme.spacing(2)};
    border-radius: ${theme.borderRadius.medium};
    background-color: ${BGColorMap[color]};
    border: 1px solid ${BorderColorMap[color]};

    @container (max-width: ${breakpoints.small.max}px) {
      flex-direction: ${responsive ? 'column' : 'row'};
      align-items: stretch;
      gap: ${theme.spacing(1)};
    }
  `,
  contentContainer: (responsive: boolean) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(2)};

    @container (max-width: ${breakpoints.small.max}px) {
      align-items: ${responsive ? 'flex-start' : 'center'};
      gap: ${theme.spacing(1)};
      flex-direction: ${responsive ? 'column' : 'row'};
    }
  `,
  content: css`
    display: flex;
    flex-direction: column;
    flex: 1;
  `,
  actionBar: css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: ${theme.spacing(1)};
    flex-shrink: 0;
  `,
  responsiveDismissIconBtn: css`
    display: block;
    @container (max-width: ${breakpoints.small.max}px) {
      display: none;
    }
  `,
  responsiveDismissBtn: css`
    display: none;
    color: ${theme.colors.neutral50};
    @container (max-width: ${breakpoints.small.max}px) {
      display: block;
    }
  `,
};
